import type { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import styled, { type Interpolation } from 'styled-components';

import BaseClass from 'components/ui/shared/BaseClass';
import { ElementTestId } from 'enums/testing';
import { deferredRenderCall } from 'utils/renderingUtils';

import { OVERLAY_DARK, OVERLAY_REDUCED_MOTION } from 'styles/blanket';
import { SECTION_PADDING } from 'styles/spacing';
import { BORDER_RADIUS_0, BORDER_RADIUS_300, SPACE_200, SPACE_400 } from 'styles/tokens';

const Container = styled.div<{ $isVisible: boolean }>`
  height: 100vh;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  position: relative;
  width: 100%;
`;

const Overlay = styled.div`
  background-color: ${OVERLAY_DARK};
  height: 100vh;
  position: absolute;
  width: 100%;

  @media (prefers-reduced-motion) {
    background-color: ${OVERLAY_REDUCED_MOTION};
  }
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const ContentInner = styled.div<{
  $contentStyleOverride?: Interpolation<object>;
  $isFixedToTop?: boolean;
  $showBorderRadius?: boolean;
}>`
  align-self: ${props => (props.$isFixedToTop ? 'flex-start' : 'center')};
  border-radius: ${props => (props.$showBorderRadius ? BORDER_RADIUS_300 : BORDER_RADIUS_0)};
  box-shadow:
    0 ${SPACE_200} ${SPACE_400} 0 rgba(22, 37, 50, 0.15),
    0 0 1px 0 rgba(22, 37, 50, 0.24);
  margin: 200px ${SECTION_PADDING};
  overflow: hidden;
  position: relative;
  ${({ $contentStyleOverride }) => $contentStyleOverride};
`;

export interface Props {
  children?: ReactNode;
  contentStyleOverride?: Interpolation<object>;
  /**
   * Whether onCancel will be called on overlay click and ESC keydown
   */
  isDismissible?: boolean;
  isFixedToTop?: boolean;
  isOpen: boolean;
  /**
   * False: absolute positioned child, true: root portal container
   */
  isPortalDialog?: boolean;
  /**
   * Used to specify the the parent node in which a dialog child node will be appended
   */
  modalRootElement?: HTMLElement;
  /**
   * When isPortalDialog, used to specify the id of the parent node in which a dialog child node will be appended.
   * By default, will search for a DOM node with an id of 'modal-root' to use as the portal root.
   */
  modalRootId?: string;
  /**
   * A callback that runs on overlay click and ESC keydown when isDismissible
   */
  onCancel?: () => void;
  showBorderRadius?: boolean;
  testId?: string;
}
interface State {
  isVisible: boolean;
}

/**
 * Generic Controlled Dialog component.
 * Based on eBlock Dialog component.
 *
 * Use to compose more specific dialogs e.g. AddEntityDialog.
 * See DialogContent.tsx for components to help with composition.
 */
export default class Dialog extends BaseClass<Props, State> {
  static defaultProps = {
    isPortalDialog: true,
  };
  dialog: Element;

  modalRoot: HTMLElement | null;

  state = { isVisible: false };

  constructor(props: Props) {
    super(props);
    this.modalRoot = this.getModalRoot();
    this.dialog = document.createElement('div');
    this.dialog.className = 'modal-dialog';
  }

  componentDidMount() {
    super.componentDidMount();
    globalThis.addEventListener('keydown', this.handleKeyDown);

    /*
     * A consumer that conditionally mounts this component already open (rather than mounting it
     * once and toggling `isOpen`) never fires the `false -> true` transition componentDidUpdate
     * looks for, so without this the portal target is created but never attached to modalRoot --
     * nothing renders, and there is no further update to trigger it since Apollo can serve a
     * repeat query's data synchronously with no loading transition.
     */
    if (!this.props.isOpen) {
      return;
    }

    this.modalRoot = this.getModalRoot();
    this.modalRoot?.append(this.dialog);
    deferredRenderCall(() => this.setState({ isVisible: true }));
  }

  componentDidUpdate() {
    const { isOpen } = this.props;
    const { isVisible } = this.state;

    this.modalRoot = this.getModalRoot();

    if (isOpen && !isVisible) {
      this.modalRoot?.append(this.dialog);
      deferredRenderCall(() => this.setState({ isVisible: true }));
    } else if (!isOpen && isVisible) {
      this.removeDialog();
      this.setState({ isVisible: false });
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.removeDialog();
    globalThis.removeEventListener('keydown', this.handleKeyDown);
  }

  getModalRoot = () =>
    this.props.modalRootElement || !!this.props.isPortalDialog
      ? document.querySelector<HTMLDivElement>(`#${this.props.modalRootId || 'modal-root'}`)
      : null;

  handleClose = () => {
    const { isDismissible, isOpen, onCancel } = this.props;

    if (isOpen && isDismissible !== false) {
      onCancel?.();
    }
  };

  handleKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === 27) {
      this.handleClose();
    }
  };

  removeDialog = () => {
    if (this.dialog.parentNode === this.modalRoot) {
      this.dialog?.remove();
    }
  };

  render() {
    const { isOpen, isPortalDialog } = this.props;

    if (!isOpen) {
      return null;
    }

    return isPortalDialog ? (
      ReactDOM.createPortal(this.renderDialog(), this.dialog)
    ) : (
      <div className="modal-dialog">{this.renderDialog()}</div>
    );
  }

  renderDialog = () => {
    const { children, contentStyleOverride, isFixedToTop, showBorderRadius = true, testId } = this.props;
    const { isVisible } = this.state;

    return (
      <Container $isVisible={isVisible} data-testid={testId} role="dialog">
        <Overlay data-testid={ElementTestId.DIALOG_BACKGROUND} onClick={this.handleClose} />
        <Content>
          <ContentInner
            $contentStyleOverride={contentStyleOverride}
            $isFixedToTop={isFixedToTop}
            $showBorderRadius={showBorderRadius}
          >
            {children}
          </ContentInner>
        </Content>
      </Container>
    );
  };
}
